import { useState, useMemo, useEffect } from 'react'
import {
  Users, Search, Scan, ShieldCheck, X, ChevronLeft, ChevronRight,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { toast } from 'sonner'
import {
  useLeadsPages, useLeadsStatus, useLeadsContacts,
  useScanAll, useQualifyAll, useScanPage, useQualifyPage, useCancelJob,
  type LeadPage, type LeadStatus,
} from '@/api/hooks/useLeads'

function formatNum(n: number): string {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M'
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'k'
  return String(n)
}

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return '\u2014'
  const diff = Date.now() - new Date(dateStr).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'agora'
  if (mins < 60) return `${mins}min`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h`
  const days = Math.floor(hours / 24)
  return `${days}d`
}

type PageStatus = 'idle' | 'queued' | 'scanning' | 'qualifying' | 'done'

function getPageStatusFromLiveStatus(pageId: string, statuses: LeadStatus[]): { status: PageStatus; detail?: string } | null {
  const s = statuses.find(s => s.pageId === pageId)
  if (!s) return null

  if (s.qualify?.status === 'processing') {
    const pct = s.qualify.total > 0 ? Math.round((s.qualify.processed / s.qualify.total) * 100) : 0
    return { status: 'qualifying', detail: `Qualifying ${pct}%` }
  }
  if (s.scan?.status === 'processing') {
    return { status: 'scanning', detail: `Scanning... ${formatNum(s.scan.imported)}` }
  }
  if (s.qualify?.status === 'queued' || s.scan?.status === 'queued') {
    return { status: 'queued', detail: 'Na fila' }
  }
  if (s.scan?.status === 'completed' || s.qualify?.status === 'completed') {
    return { status: 'done' }
  }
  return { status: 'idle' }
}

function getPageStatusFromPage(page: LeadPage): { status: PageStatus; detail?: string } {
  if (page.lastQualify?.status === 'processing') {
    const pct = page.lastQualify.total > 0
      ? Math.round((page.lastQualify.processed / page.lastQualify.total) * 100)
      : 0
    return { status: 'qualifying', detail: `Qualifying ${pct}%` }
  }

  if (page.lastScan?.status === 'processing') {
    return { status: 'scanning', detail: `Scanning... ${formatNum(page.lastScan.imported)}` }
  }

  if (page.lastQualify?.status === 'queued' || page.lastScan?.status === 'queued') {
    return { status: 'queued', detail: 'Na fila' }
  }

  if (page.lastScan?.status === 'completed' || page.lastQualify?.status === 'completed') {
    return { status: 'done' }
  }

  return { status: 'idle' }
}

function getPageStatus(page: LeadPage, statuses: LeadStatus[]): { status: PageStatus; detail?: string } {
  return getPageStatusFromLiveStatus(page.pageId, statuses) ?? getPageStatusFromPage(page)
}

export default function LeadsPage() {
  const [selectedPageId, setSelectedPageId] = useState<string | null>(null)
  const [pageSearch, setPageSearch] = useState('')
  const [contactSearch, setContactSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [contactPage, setContactPage] = useState(1)

  const { data: pages = [], isLoading: pagesLoading } = useLeadsPages()
  const hasPageActiveJobs = useMemo(() =>
    pages.some(page =>
      page.lastScan?.status === 'processing' || page.lastScan?.status === 'queued' ||
      page.lastQualify?.status === 'processing' || page.lastQualify?.status === 'queued'
    ), [pages])
  const { data: statuses = [] } = useLeadsStatus(hasPageActiveJobs ? 3000 : false)
  const hasActiveJobs = useMemo(() =>
    hasPageActiveJobs || statuses.some(s =>
      s.scan?.status === 'processing' || s.scan?.status === 'queued' ||
      s.qualify?.status === 'processing' || s.qualify?.status === 'queued'
    ), [hasPageActiveJobs, statuses])
  const { data: livePages = [] } = useLeadsPages(hasActiveJobs ? 3000 : false)
  const currentPages = hasActiveJobs ? livePages : pages

  const selectedPage = useMemo(() =>
    currentPages.find(p => p.pageId === selectedPageId) ?? null, [currentPages, selectedPageId])

  const { data: contactsData } = useLeadsContacts(selectedPageId, {
    page: contactPage,
    status: statusFilter === 'all' ? undefined : statusFilter,
    search: contactSearch || undefined,
  })

  const scanAll = useScanAll()
  const qualifyAll = useQualifyAll()
  const scanPage = useScanPage()
  const qualifyPage = useQualifyPage()
  const cancelJob = useCancelJob()

  const handleScanAll = () => {
    scanAll.mutate(undefined, {
      onSuccess: (data) => {
        if (data.dispatched > 0) {
          toast.success(`Scan iniciado em ${data.dispatched} pagina(s)`)
          return
        }
        if (data.cooldownSkipped > 0) {
          toast.error(`Scan bloqueado. Aguarde ${data.cooldownHours}h para rodar novamente.`)
          return
        }
        toast.message('Ja existe scan ativo ou recente para essas paginas')
      },
      onError: (error: Error) => {
        toast.error(error.message || 'Erro ao iniciar scan')
      },
    })
  }

  const handleScanPage = (pageId: string) => {
    scanPage.mutate(pageId, {
      onSuccess: () => {
        toast.success('Scan iniciado')
      },
      onError: (error: Error) => {
        toast.error(error.message || 'Erro ao iniciar scan')
      },
    })
  }

  // Auto-select first page
  useEffect(() => {
    if (!selectedPageId && pages.length > 0) {
      setSelectedPageId(currentPages[0].pageId)
    }
  }, [currentPages, selectedPageId])

  // Reset contact page on filter change
  useEffect(() => { setContactPage(1) }, [statusFilter, contactSearch, selectedPageId])

  const filteredPages = useMemo(() => {
    if (!pageSearch) return currentPages
    const q = pageSearch.toLowerCase()
    return currentPages.filter(p => p.name.toLowerCase().includes(q))
  }, [currentPages, pageSearch])

  const pageStatus = selectedPage ? getPageStatus(selectedPage, statuses) : null
  const isPageActive = pageStatus?.status === 'scanning' || pageStatus?.status === 'qualifying'
  const progressSummary = useMemo(() => {
    if (currentPages.length === 0) {
      return null
    }

    const byStatus = currentPages.reduce((acc, page) => {
      const status = getPageStatus(page, statuses).status
      acc[status] = (acc[status] ?? 0) + 1
      return acc
    }, {} as Record<PageStatus, number>)

    const queued = byStatus.queued ?? 0
    const scanning = byStatus.scanning ?? 0
    const qualifying = byStatus.qualifying ?? 0
    const active = queued + scanning + qualifying
    const done = byStatus.done ?? 0
    const total = currentPages.length
    const completed = done + (byStatus.idle ?? 0)
    const percent = total > 0 ? Math.round((completed / total) * 100) : 0

    if (active === 0) {
      return null
    }

    return {
      total,
      queued,
      scanning,
      qualifying,
      active,
      completed,
      percent,
    }
  }, [currentPages, statuses])

  return (
    <div className="flex flex-col h-[calc(100vh-80px)]">
      {/* Header */}
      <div className="flex items-start justify-between mb-4 gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-primary/15 to-primary/5 border border-primary/10">
            <Users className="h-5 w-5 text-primary" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl font-bold tracking-tight">Leads</h1>
            <p className="text-xs text-muted-foreground/70">
              {formatNum(currentPages.reduce((s, p) => s + p.total, 0))} contatos em {currentPages.length} paginas
            </p>
            {progressSummary && (
              <div className="mt-2 w-full max-w-[420px] rounded-xl border border-primary/15 bg-primary/5 px-3 py-2">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[11px] font-semibold text-foreground">
                      {progressSummary.completed}/{progressSummary.total} paginas concluidas
                    </div>
                    <div className="text-[10px] text-muted-foreground">
                      {progressSummary.scanning} escaneando, {progressSummary.qualifying} qualificando, {progressSummary.queued} na fila
                    </div>
                  </div>
                  <Badge variant="outline" className="border-primary/20 bg-background/70 text-[10px] text-primary">
                    {progressSummary.percent}%
                  </Badge>
                </div>
                <div className="mt-2 h-2 overflow-hidden rounded-full bg-primary/10">
                  <div
                    className="h-full rounded-full bg-primary transition-all duration-500"
                    style={{ width: `${progressSummary.percent}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={handleScanAll} disabled={scanAll.isPending}>
            <Scan className="h-3.5 w-3.5 mr-1.5" />
            Scan All
          </Button>
          <Button size="sm" variant="outline" onClick={() => qualifyAll.mutate()} disabled={qualifyAll.isPending}
            className="border-emerald-500/30 text-emerald-500 hover:bg-emerald-500/10">
            <ShieldCheck className="h-3.5 w-3.5 mr-1.5" />
            Qualify All
          </Button>
        </div>
      </div>

      {/* Two Panel Layout */}
      <div className="flex gap-3 flex-1 min-h-0">
        {/* Left Panel — Pages */}
        <div className="w-[280px] min-w-[280px] rounded-2xl border border-border/30 bg-card/50 backdrop-blur-sm flex flex-col overflow-hidden">
          <div className="p-3 border-b border-border/30">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground/50" />
              <Input
                placeholder="Buscar pagina..."
                value={pageSearch}
                onChange={e => setPageSearch(e.target.value)}
                className="h-8 pl-8 text-xs rounded-lg bg-muted/20 border-transparent"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-1">
            {pagesLoading ? (
              <p className="text-xs text-muted-foreground p-3">Carregando...</p>
            ) : filteredPages.map(page => {
              const ps = getPageStatus(page, statuses)
              const isActive = page.pageId === selectedPageId
              return (
                <button
                  key={page.pageId}
                  onClick={() => setSelectedPageId(page.pageId)}
                  className={`w-full flex items-center gap-2.5 p-2.5 rounded-xl text-left transition-colors ${
                    isActive
                      ? 'bg-primary/10 border border-primary/20'
                      : 'border border-transparent hover:bg-muted/15'
                  }`}
                >
                  <img
                    src={page.picture}
                    alt=""
                    className="w-8 h-8 rounded-lg bg-card object-cover"
                    onError={e => {
                      (e.target as HTMLImageElement).style.display = 'none'
                    }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] font-semibold truncate">{page.name}</div>
                    <div className="text-[9px] text-muted-foreground flex items-center gap-1.5">
                      <span className={`inline-block w-1.5 h-1.5 rounded-full ${
                        ps.status === 'done' ? 'bg-emerald-400' :
                        ps.status === 'scanning' ? 'bg-primary animate-pulse' :
                        ps.status === 'qualifying' ? 'bg-yellow-400 animate-pulse' :
                        ps.status === 'queued' ? 'bg-muted-foreground/50' :
                        'bg-muted-foreground/30'
                      }`} />
                      {ps.detail || `${formatNum(page.total)} contatos`}
                    </div>
                  </div>
                </button>
              )
            })}
          </div>
        </div>

        {/* Right Panel — Page Detail */}
        <div className="flex-1 rounded-2xl border border-border/30 bg-card/50 backdrop-blur-sm flex flex-col overflow-hidden">
          {!selectedPage ? (
            <div className="flex-1 flex items-center justify-center text-muted-foreground/50 text-sm">
              Selecione uma pagina
            </div>
          ) : (
            <>
              {/* Page Header */}
              <div className="flex items-center justify-between p-4 border-b border-border/30">
                <div className="flex items-center gap-3">
                  <img src={selectedPage.picture} alt="" className="w-7 h-7 rounded-md bg-card" />
                  <span className="font-bold text-sm">{selectedPage.name}</span>
                  {pageStatus?.detail && (
                    <Badge variant="secondary" className="text-[10px]">{pageStatus.detail}</Badge>
                  )}
                  {hasActiveJobs && (
                    <Badge variant="outline" className="text-[10px] border-primary/20 text-primary">
                      Atualizando...
                    </Badge>
                  )}
                </div>
                <div className="flex gap-2">
                  {isPageActive && (
                    <Button size="sm" variant="destructive" className="h-7 text-[10px]"
                      onClick={() => cancelJob.mutate(selectedPage.pageId)}>
                      <X className="h-3 w-3 mr-1" /> Cancelar
                    </Button>
                  )}
                  <Button size="sm" variant="outline" className="h-7 text-[10px]"
                    onClick={() => handleScanPage(selectedPage.pageId)}
                    disabled={pageStatus?.status === 'scanning'}>
                    <Scan className="h-3 w-3 mr-1" /> Scan
                  </Button>
                  <Button size="sm" variant="outline"
                    className="h-7 text-[10px] border-emerald-500/30 text-emerald-500 hover:bg-emerald-500/10"
                    onClick={() => qualifyPage.mutate(selectedPage.pageId)}
                    disabled={pageStatus?.status === 'qualifying'}>
                    <ShieldCheck className="h-3 w-3 mr-1" /> Qualify
                  </Button>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-4 gap-2 p-4">
                {[
                  { label: 'Total', value: selectedPage.total, color: 'text-primary' },
                  { label: 'Validos', value: selectedPage.valid, color: 'text-emerald-400' },
                  { label: 'Invalidos', value: selectedPage.invalid, color: 'text-destructive' },
                  { label: '24h', value: selectedPage.twentyFourH, color: 'text-blue-400' },
                ].map(stat => (
                  <div key={stat.label} className="rounded-xl border border-border/30 bg-background/50 p-3 text-center">
                    <div className={`text-lg font-bold tabular-nums ${stat.color}`}>
                      {formatNum(stat.value)}
                    </div>
                    <div className="text-[8px] font-semibold uppercase tracking-[1px] text-muted-foreground mt-0.5">
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>

              {/* Filters */}
              <div className="flex items-center gap-2 px-4 pb-2">
                <div className="relative flex-1">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground/50" />
                  <Input
                    placeholder="Buscar contato..."
                    value={contactSearch}
                    onChange={e => setContactSearch(e.target.value)}
                    className="h-8 pl-8 text-xs rounded-lg bg-muted/20 border-transparent"
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className={`h-8 w-[130px] rounded-lg text-xs ${
                    statusFilter !== 'all' ? 'bg-primary/10 text-primary border-primary/20' : ''
                  }`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="valid">Validos</SelectItem>
                    <SelectItem value="invalid">Invalidos</SelectItem>
                    <SelectItem value="pending">Pendentes</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Contacts Table */}
              <div className="flex-1 overflow-y-auto px-4 pb-4">
                <div className="rounded-xl border border-border/30 overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border/30">
                        <th className="text-left text-[9px] font-semibold uppercase tracking-[1.5px] text-muted-foreground/70 py-2.5 px-3 w-[35%]">Nome</th>
                        <th className="text-left text-[9px] font-semibold uppercase tracking-[1.5px] text-muted-foreground/70 py-2.5 px-3 w-[20%]">Status</th>
                        <th className="text-left text-[9px] font-semibold uppercase tracking-[1.5px] text-muted-foreground/70 py-2.5 px-3 w-[25%]">Ultimo contato</th>
                        <th className="text-left text-[9px] font-semibold uppercase tracking-[1.5px] text-muted-foreground/70 py-2.5 px-3 w-[20%]">Fonte</th>
                      </tr>
                    </thead>
                    <tbody>
                      {contactsData?.data.map(contact => (
                        <tr key={contact.id} className="border-b border-border/20 hover:bg-muted/15">
                          <td className="py-2 px-3">
                            <div className="flex items-center gap-2">
                              <div className="w-6 h-6 rounded-md bg-primary/10 flex items-center justify-center text-[10px] font-semibold text-primary flex-shrink-0">
                                {contact.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
                              </div>
                              <span className="text-[11px] truncate">{contact.name}</span>
                            </div>
                          </td>
                          <td className="py-2 px-3">
                            <Badge variant="secondary" className={`text-[10px] ${
                              contact.status === 'valid' ? 'bg-emerald-500/12 text-emerald-400' :
                              contact.status === 'invalid' ? 'bg-destructive/12 text-destructive' :
                              'bg-muted/30 text-muted-foreground'
                            }`}>
                              {contact.status === 'valid' ? 'Valido' :
                               contact.status === 'invalid' ? 'Invalido' : 'Pendente'}
                            </Badge>
                          </td>
                          <td className="py-2 px-3 text-[11px] text-muted-foreground">
                            {timeAgo(contact.last_seen_at)}
                          </td>
                          <td className="py-2 px-3 text-[11px] text-muted-foreground">
                            {contact.source === 'fb_dm' ? 'DM' :
                             contact.source === 'fb_comment' ? 'Comentario FB' :
                             contact.source === 'ig_comment' ? 'Comentario IG' :
                             contact.source}
                          </td>
                        </tr>
                      ))}
                      {contactsData?.data.length === 0 && (
                        <tr>
                          <td colSpan={4} className="text-center py-8 text-muted-foreground/50 text-xs">
                            Nenhum contato encontrado
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {contactsData && contactsData.last_page > 1 && (
                  <div className="flex items-center justify-between mt-3">
                    <span className="text-[10px] text-muted-foreground">
                      {contactsData.total} contatos — Pagina {contactsData.current_page} de {contactsData.last_page}
                    </span>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0"
                        disabled={contactsData.current_page <= 1}
                        onClick={() => setContactPage(p => p - 1)}>
                        <ChevronLeft className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0"
                        disabled={contactsData.current_page >= contactsData.last_page}
                        onClick={() => setContactPage(p => p + 1)}>
                        <ChevronRight className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
